<template>
    <div class="import-feedback-drawer-guide-sample-card__selected">
        <div
            v-if="selectedGuideName"
            class="import-feedback-drawer-guide-sample-card__header">
            <div class="import-feedback-drawer-guide-sample-card__button-wrapper">
                <pendo-button
                    class="import-feedback-drawer-guide-sample-card__button"
                    :label="selectedGuideName"
                    theme="app"
                    type="subtle"
                    :suffix-icon="hoveringOnTitle ? 'external-link' : null"
                    @mouseover="hoveringOnTitle = true"
                    @mouseleave="hoveringOnTitle = false"
                    @click="$emit('open-guide')" />
            </div>
        </div>
        <div class="import-feedback-drawer-guide-sample-card__body">
            <div
                v-if="!selectedGuide"
                class="import-feedback-drawer-guide-sample-card__no-guide-selected">
                <img
                    width="100px"
                    height="120px"
                    :src="emptyStateSrc">
                <div class="import-feedback-drawer-guide-sample-card__no-selected-title">
                    Select {{ selectedType }}
                </div>
                <div class="import-feedback-drawer-guide-sample-card__no-selected-description">
                    Select {{ selectedType }} to import poll responses and kickstart your feedback process
                </div>
            </div>
            <div v-else>
                <div class="import-feedback-drawer-guide-sample-card__responses-wrapper">
                    <p
                        v-if="showTitle"
                        class="import-feedback-drawer-guide-sample-card__responses-title">
                        {{ showTitle }}
                    </p>
                    <pendo-loading-indicator
                        v-if="guideResponseCountLoading"
                        class="import-feedback-drawer-guide-sample-card__loading" />
                    <div v-else-if="guidePollsHaveNoResponses">
                        <div class="import-feedback-drawer-guide-sample-card__no-responses">
                            <pendo-icon
                                type="alert-circle-solid"
                                stroke="var(--palette-gray-0)"
                                fill="var(--palette-yellow-70)"
                                size="24" />
                            <p class="import-feedback-drawer-guide-sample-card__no-responses-text">
                                {{ noResponsesText }}
                            </p>
                        </div>
                    </div>
                    <div
                        v-if="showNpsResponse"
                        class="import-feedback-drawer-guide-sample-card__samples">
                        <div
                            class="import-feedback-drawer-guide-sample-card__sample"
                            data-cy="import-feedback-poll-response">
                            <span
                                data-cy="import-feedback-poll-response"
                                class="import-feedback-drawer-guide-sample-card__sample-text">
                                "{{ npsReason[0] }}"
                            </span>
                            <div>
                                <div class="import-feedback-drawer-guide-sample-card__score">
                                    <div
                                        class="import-feedback-drawer-guide-sample-card__score-indicator"
                                        :style="{ background: getNpsColorByScore(npsRating) }" />
                                    <span>{{ npsRating[0] }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="!guideResponseCountLoading"
                        class="import-feedback-drawer-guide-sample-card__responses">
                        <p
                            v-if="hasAdditionalNpsPolls"
                            class="import-feedback-drawer-guide-sample-card__responses-title">
                            Additional polls
                        </p>
                        <div
                            v-for="pair in questionResponsePairs"
                            :key="pair.id"
                            class="import-feedback-drawer-guide-sample-card__response-wrapper">
                            <p
                                class="import-feedback-drawer-guide-sample-card__question"
                                data-cy="import-feedback-poll-question">
                                {{ pair.question }}
                            </p>
                            <p
                                class="import-feedback-drawer-guide-sample-card__response"
                                data-cy="import-feedback-poll-response">
                                {{ formatResponse(pair.response) }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="selectedGuide"
            class="import-feedback-drawer-guide-sample-card__footer">
            <pendo-divider
                v-if="showDivider"
                height="1px"
                class="import-feedback-drawer-guide-sample-card__divider" />
            <div class="import-feedback-drawer-guide-sample-card__details">
                <p class="import-feedback-drawer-guide-sample-card__details-text">
                    Responses in the last 90 days:
                </p>
                <div
                    v-pendo-loading="guideResponseCountLoading"
                    data-cy="import-feedback-poll-response-count"
                    class="import-feedback-drawer-guide-sample-card__details-content">
                    {{ selectedGuideResponseCount }}
                </div>
            </div>
            <div class="import-feedback-drawer-guide-sample-card__details">
                <p class="import-feedback-drawer-guide-sample-card__details-text">
                    Connected to Listen:
                </p>
                <div class="import-feedback-drawer-guide-sample-card__details-content">
                    <pendo-icon
                        :type="connectedConfig.icon"
                        :size="18"
                        stroke-width="2"
                        :stroke="connectedConfig.stroke" />
                    {{ connectedConfig.text }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { PendoLoadingIndicator, PendoIcon, PendoLoading, PendoButton, PendoDivider } from '@pendo/components';
import { sanitizePollQuestion, sanitizePollResponse } from '@/utils/guide.utils';
import { npsColorByScore } from '@/net-promoter-system/helpers/common.util.js';

export default {
    name: 'ImportFeedbackDrawerGuideSampleCard',
    components: {
        PendoLoadingIndicator,
        PendoIcon,
        PendoButton,
        PendoDivider,
    },
    directives: {
        PendoLoading,
    },
    props: {
        selectedGuide: {
            type: Object,
            default: null,
        },
        selectedGuideResponses: {
            type: Array,
            default: () => [],
        },
        selectedGuideResponseCount: {
            type: Number,
            default: 0,
        },
        fetchingSourceConfig: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: 'guide',
        },
    },
    emits: ['open-guide', 'change-poll-question'],
    data () {
        return {
            hoveringOnTitle: false,
        };
    },
    computed: {
        selectedGuideName () {
            return this.selectedGuide?.name;
        },
        isGuideType () {
            return this.type === 'guide';
        },
        emptyStateSrc () {
            return this.isGuideType
                ? require('@img/voc/no-guide-selected.svg')
                : require('@img/voc/no-nps-selected.svg');
        },
        questionResponsePairs () {
            if (this.isGuideType) {
                return this.selectedGuide.polls.map((poll) => {
                    const matchedResponse = this.selectedGuideResponses?.find(
                        (response) => response.pollId === poll.id,
                    );

                    return {
                        id: poll.id,
                        question: sanitizePollQuestion(poll.question),
                        response: sanitizePollResponse(matchedResponse?.pollResponse, poll?.attributes?.type),
                        noResponse: !matchedResponse,
                    };
                });
            }

            return this.selectedGuide.polls
                .filter((poll) => !(poll.attributes.type === 'NPSRating' || poll.attributes.type === 'NPSReason'))
                .map((poll) => {
                    const matchedResponse = this.selectedGuideResponses?.find((response) =>
                        response.pollResponses?.some((pollResponse) => pollResponse.pollId === poll.id),
                    );

                    const nonNpsResponse = matchedResponse
                        ? matchedResponse.pollResponses.find((pollResponse) => pollResponse.pollId === poll.id)
                            .pollResponse
                        : null;

                    let formattedResponse = nonNpsResponse;

                    if (poll.attributes.type === 'PickList' && nonNpsResponse) {
                        const responseValue = poll.idResponses?.[nonNpsResponse];
                        formattedResponse = responseValue || 'Deleted option';
                    }

                    formattedResponse = sanitizePollResponse(formattedResponse);

                    if (poll.attributes.type === 'PositiveNegative' && formattedResponse) {
                        if (formattedResponse === 1) {
                            formattedResponse = 'Yes';
                        } else if (formattedResponse === 0) {
                            formattedResponse = 'No';
                        }
                    }

                    return {
                        id: poll.id,
                        question: sanitizePollQuestion(poll.question),
                        response: formattedResponse,
                        noResponse: !nonNpsResponse,
                    };
                });
        },
        guideResponseCountLoading () {
            return !this.selectedGuideResponses || this.fetchingSourceConfig;
        },
        connectedConfig () {
            if (this.selectedGuide.autoCreateFeedback) {
                return {
                    text: 'Yes',
                    icon: 'check',
                    stroke: 'var(--palette-green-90)',
                };
            }

            return {
                text: 'No',
                icon: 'x',
                stroke: 'var(--palette-red-100)',
            };
        },
        guidePollsHaveNoResponses () {
            return this.somePollsHaveNoResponses && this.isGuideType;
        },
        somePollsHaveNoResponses () {
            return this.questionResponsePairs.some((pair) => pair.noResponse);
        },
        noResponsesTotal () {
            return this.questionResponsePairs.every((pair) => pair.noResponse);
        },
        formatResponse () {
            return (response) => {
                if (!this.isGuideType && !response) {
                    return 'Not answered';
                }

                return response ? `"${response}"` : '0 responses';
            };
        },
        noResponsesText () {
            if (this.noResponsesTotal) {
                return 'None of the polls in this guide have had responses yet, however any future responses can be connected to Listen.';
            }

            return 'Some poll questions in this guide have no responses, however any future responses can be connected to Listen.';
        },
        getNpsColorByScore () {
            return (resp) => npsColorByScore(resp);
        },
        npsRating () {
            return this.selectedGuideResponses.map((response) => {
                if (response.pollResponses) {
                    const npsRatings = response.pollResponses.filter((poll) => poll.pollType === 'NPSRating');
                    const latestNpsRating = npsRatings[npsRatings.length - 1];

                    return latestNpsRating ? latestNpsRating.pollResponse : null;
                }

                return null;
            });
        },
        npsReason () {
            return this.selectedGuideResponses.map((response) => {
                if (response.pollResponses) {
                    const npsReasons = response.pollResponses.filter((poll) => poll.pollType === 'NPSReason');
                    const latestNpsReason = npsReasons[npsReasons.length - 1];

                    return latestNpsReason ? sanitizePollResponse(latestNpsReason.pollResponse) : null;
                }

                return null;
            });
        },
        allNpsPollResponses () {
            if (!this.isGuideType) {
                return this.selectedGuide.polls
                    .filter((poll) => poll.attributes.type === 'NPSRating' || poll.attributes.type === 'NPSReason')
                    .map((poll) => {
                        const matchedResponse = this.selectedGuideResponses?.find((response) =>
                            response.pollResponses?.some((pollResponse) => pollResponse.pollId === poll.id),
                        );
                        const npsResponse = matchedResponse
                            ? matchedResponse.pollResponses.find((pollResponse) => pollResponse.pollId === poll.id)
                                .pollResponse
                            : null;

                        return {
                            id: poll.id,
                            question: poll.question,
                            response: npsResponse,
                            noResponse: !npsResponse,
                        };
                    });
            }

            return [];
        },
        hasAdditionalNpsPolls () {
            if (this.isGuideType) {
                return false;
            }

            return this.selectedGuide.polls.some(
                (poll) => !(poll.attributes.type === 'NPSRating' || poll.attributes.type === 'NPSReason'),
            );
        },
        npsHasResponses () {
            return this.allNpsPollResponses.some((poll) => !poll.noResponse);
        },
        showNpsResponse () {
            return !this.isGuideType && !this.guideResponseCountLoading && this.npsHasResponses;
        },
        selectedType () {
            return this.isGuideType ? 'a guide' : 'an NPS survey';
        },
        showTitle () {
            if (this.isGuideType) return 'Poll questions & responses (last 90 days)';
            if (!this.isGuideType && this.npsHasResponses) {
                return 'Sample of the latest response & score (last 90 days)';
            }

            return null;
        },
        showDivider () {
            return (
                (!this.guideResponseCountLoading && this.selectedGuideResponses.length > 0) ||
                this.hasAdditionalNpsPolls
            );
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/voc/styles/voc.style.less';
.import-feedback-drawer-guide-sample-card {
    &__selected {
        position: sticky;
        top: -50px;
        width: 100%;
        border: 1px solid var(--palette-gray-40);
        border-radius: 3px;
        max-height: 400px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }
    &__body {
        overflow: auto;
        max-height: 400px;
        padding: 16px 16px 0 16px;
    }
    &__responses-title {
        line-height: 150%;
        font-weight: 600;
    }
    &__response-wrapper {
        border-left: 3px solid var(--palette-gray-50);
    }
    &__question {
        line-height: 150%;
        margin-left: 7px;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    &__response {
        line-height: 150%;
        margin-left: 26px;
        font-size: 13px;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    &__header {
        border-bottom: 1px solid var(--palette-gray-40);
        min-height: 48px;
        padding: 0 16px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }
    &__footer {
        padding: 0 16px 16px 16px;
    }
    &__no-guide-selected {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
    &__no-selected-title {
        font-size: 22px;
        font-weight: 600;
        line-height: 130%;
        margin-bottom: 4px;
    }
    &__no-selected-description {
        font-size: 14px;
        line-height: 130%;
        text-align: center;
        margin-bottom: 26px;
    }
    &__sample-text {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    &__details {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 4px;
        line-height: 150%;
        overflow: hidden;
        padding: 4px 0;
    }
    &__details-text {
        font-weight: 600;
        white-space: nowrap;
        margin: 0;
    }
    &__details-content {
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        min-width: 30px;
        min-height: 24px;
        margin: 0;
        gap: 3px;
    }
    &__no-responses {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 16px;
        border: 1px solid var(--palette-yellow-70);
        background-color: var(--palette-yellow-20);
        border-radius: 3px;
        margin-bottom: 12px;
    }
    &__no-responses-text {
        margin: 0;
        line-height: 150%;
    }
    &__button {
        font-size: 16px;
        width: 100%;
        :deep(span) {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
    &__loading {
        margin-bottom: 40px;
    }
    &__button-wrapper {
        overflow: hidden;
    }
    &__responses-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }
    &__divider {
        margin-bottom: 8px;
    }
    &__samples {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    &__sample {
        border-left: 3px solid var(--palette-gray-50);
        line-height: 150%;
        padding: 8px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-content: flex-start;
        gap: 5px;
        margin-bottom: 8px;
    }
    &__score {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
    }
    &__score-indicator {
        height: 9px;
        width: 9px;
        border-radius: 50%;
    }
}
</style>
